// ─────────────────────────────────────────────────────────────────────────────
// firstRunSync, day-one data warm-up. (2026-07-18, Ryan: "we need to make sure
// a new artist has a way to push all their data on a new account for the first
// time. I dont want them sitting with a half empty account for a day.")
//
// WHY this exists: nearly every panel on a new artist's account fills LAZILY,
// social snapshot + Songstats audience warm on the first dashboard load, the
// music compose (top tracks / playlists / latest release) warms on the first
// Music-tab load, and RADIO/SXM warms ONLY when a rotating daily cron reaches
// the artist (which can be days). So a fresh account looks half-broken until
// those triggers each independently fire. This chains all of them into ONE
// pass so the account is full immediately.
//
// Triggers (both, per Ryan):
//   • AUTO, fired on first connect (SettingsDrawer prime) AND on first
//     dashboard mount if unsynced (catches the OAuth-redirect path that
//     bypasses the settings-drawer prime).
//   • BUTTON, "Refresh all data" → firstRunSync(true), the manual escape
//     hatch + re-sync.
//
// Quota safety (non-negotiable, burning a new artist's Songstats monthly quota
// on day one is exactly the failure we're avoiding):
//   • The stats pull always runs when invoked (callers throttle it), it's the
//     same nocache pull the connect-prime already does.
//   • The EXPENSIVE once-only steps (radio refresh + music compose) are gated
//     behind a per-account marker; they run once per account per browser unless
//     `force` (the button) is passed. The radio endpoint additionally enforces
//     a 5-min server-side per-band throttle, so even the button can't be looped
//     to drain quota.
// ─────────────────────────────────────────────────────────────────────────────

import { AUTH_WORKER } from './api';
import { authHeaders, getCurrentBandId } from './auth';
import { fetchConnections, fetchDashboardStats, accountStorageScope, getCachedUser } from './dashboard';

const SYNC_MARKER_PREFIX = 'ac_first_run_sync_';

/** Per-account marker key (band id, else 'u_'+uid), never a bare 'self', so it
 *  can't leak the synced-state across logins on a shared browser. Null when no
 *  account is resolvable (fail closed → treated as "already done" for the auto
 *  path so we never fire against an unknown scope). */
function markerKey(): string | null {
  const scope = accountStorageScope();
  return scope ? SYNC_MARKER_PREFIX + scope : null;
}

/** True once the expensive once-only steps have run for this account. */
export function firstRunSyncDone(): boolean {
  const k = markerKey();
  if (!k) return true;
  try {
    return !!localStorage.getItem(k);
  } catch {
    return false;
  }
}

/** Resolve the band root this seat acts on, band id, else the member's band
 *  root (band_user_id), else their own id (a band-primary IS its own root). */
function resolveBandId(): string {
  const u = getCachedUser();
  return (
    getCurrentBandId() ||
    (u?.band_user_id != null ? String(u.band_user_id) : '') ||
    (u?.id != null ? String(u.id) : '')
  );
}

export interface FirstRunSyncResult {
  readonly stats: boolean;
  readonly radio: boolean;
  readonly music: boolean;
  readonly ranOnceOnly: boolean;
}

let _syncInFlight = false;

/**
 * Warm every lazily-filled pipeline for the acting band.
 * @param force  true (button) → run the once-only steps even if the marker is
 *               set. false (auto) → run them only if unsynced.
 * Sequential + best-effort: a failing step never aborts the rest.
 */
export async function firstRunSync(force = false): Promise<FirstRunSyncResult> {
  const result = { stats: false, radio: false, music: false, ranOnceOnly: false };
  const k = markerKey();
  if (!k) return result; // no resolvable account → no-op
  if (_syncInFlight) return result;
  _syncInFlight = true;
  try {
    let alreadySynced = false;
    try {
      alreadySynced = !!localStorage.getItem(k);
    } catch {
      /* storage blocked, treat as not-synced so the button still works */
    }
    const runOnceOnly = force || !alreadySynced;

    // 1. Social snapshot + Songstats audience (top cities / Apple / Amazon) +
    //    daily-trends seed. Always runs when invoked (the same nocache pull the
    //    connect-prime does); callers throttle. This is what fills the
    //    dashboard's follower/listener/streams tiles + the audience rail.
    try {
      await fetchDashboardStats(await fetchConnections(), { bustCache: true });
      result.stats = true;
    } catch {
      /* best-effort */
    }

    if (runOnceOnly) {
      // 2. Radio + SXM. The one pipeline with NO client-triggered warm path,
      //    otherwise it fills only when the daily rotation cron reaches this
      //    artist. Server enforces a 5-min per-band throttle.
      try {
        const rr = await fetch(AUTH_WORKER + '/api/radio/refresh', {
          method: 'POST',
          headers: authHeaders('POST'),
          body: '{}',
        });
        result.radio = rr.ok;
      } catch {
        /* best-effort */
      }

      // 3. Music compose warm, resolves Spotify track ids, harvests
      //    popularity, aggregates the playlist wall, and resolves the latest
      //    release, writing every cache so the first Music-tab visit is instant
      //    and full instead of cold.
      try {
        const bandId = resolveBandId();
        if (bandId) {
          const mr = await fetch(
            AUTH_WORKER + '/api/label/artist/' + encodeURIComponent(bandId) + '/music?window=28D&nocache=1',
            { headers: authHeaders('GET') },
          );
          result.music = mr.ok;
        }
      } catch {
        /* best-effort */
      }

      try {
        localStorage.setItem(k, String(Date.now()));
      } catch {
        /* storage blocked */
      }
      result.ranOnceOnly = true;
    }
  } finally {
    _syncInFlight = false;
  }
  return result;
}
